// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { gql, useMutation } from '@apollo/client';
import { useGuestRideActions } from '../hooks/useGuestRideActions';
import { useUser } from '@store';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { cancelProviderRide } from '../services/ridesApi';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography, Button } from '@mui/material';
import { BiSolidError } from 'react-icons/bi';

import Modal from '@components/ModalComponents/Modal';
import ModalHeader from '@components/ModalComponents/ModalHeader';
import ModalContent from '@components/ModalComponents/ModalContent';
import ModalFooter from '@components/ModalComponents/ModalFooter';

// GRAPHQL ---------------------------------------------------------------- //

const CANCEL_GUESTRIDE = gql`
  mutation CancelGuestride($id: bigint!, $updated_by: String) {
    update_guestrides_by_pk(pk_columns: { id: $id }, _set: { status: "cancelled", updated_by: $updated_by }) {
      id
      status
    }
  }
`;

// TYPES ---------------------------------------------------------------- //

interface CancelRideModalProps {
  open: boolean;
  onClose: () => void;
  ride: any;
}

// COMPONENT ---------------------------------------------------------------- //

const CancelRideModal: React.FC<CancelRideModalProps> = ({ open, onClose, ride }) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { refetch } = useGuestRideActions();
  const user = useUser();

  const [cancelGuestride, { loading: mutationLoading }] = useMutation(CANCEL_GUESTRIDE);
  const [cancellingProvider, setCancellingProvider] = React.useState(false);
  const loading = mutationLoading || cancellingProvider;

  const handleConfirm = async () => {
    // The latest ridehail is the provider ride. If the ride was booked, we must
    // cancel it provider-side or the live ridehail re-syncs its status back over
    // our `cancelled` write. The rides API takes the internal `ridehails.id`.
    // A ride with no provider booking (never called, or held for approval) has
    // nothing to cancel provider-side, so we skip straight to the status write.
    const latest = ride?.ridehails?.[0];
    const ridehailId: number | undefined = latest?.external_id ? latest.id : undefined;

    if (ridehailId != null) {
      setCancellingProvider(true);
      try {
        await cancelProviderRide(ridehailId);
      } catch (providerErr) {
        const axiosErr = providerErr as { response?: { status?: number; data?: { reason?: string } } };
        const httpStatus = axiosErr.response?.status;
        const reason = axiosErr.response?.data?.reason;
        // The rides API returns 409 when the provider ride isn't cancellable:
        // already cancelled / completed / in progress / auto. "Already cancelled"
        // is benign — the provider ride is already gone, so fall through and
        // reconcile the guestride. The others must NOT be flipped to cancelled
        // (e.g. a completed ride), so surface the provider's reason and refetch
        // to let the true status show.
        if (httpStatus === 409) {
          if (!/already cancelled/i.test(reason ?? '')) {
            toast.error(reason || 'This ride can no longer be cancelled.');
            if (refetch) refetch();
            onClose();
            return;
          }
        } else {
          console.error('Failed to cancel provider ride:', providerErr);
          Sentry.captureException(providerErr, {
            tags: { feature: 'guest-rides', action: 'cancel-ride' },
            extra: { rideId: ride?.id, status: ride?.status, ridehailId },
          });
          toast.error('Failed to cancel ride');
          return;
        }
      } finally {
        setCancellingProvider(false);
      }
    }

    try {
      await cancelGuestride({
        variables: {
          id: ride?.id,
          updated_by: user?.profile?.email || null,
        },
      });

      toast.success('Ride cancelled');
      if (refetch) refetch();
      onClose();
    } catch (err) {
      console.error('Failed to cancel ride:', err);
      Sentry.captureException(err, {
        tags: { feature: 'guest-rides', action: 'cancel-ride' },
        extra: { rideId: ride?.id, status: ride?.status, ridehailId },
      });
      toast.error('Failed to cancel ride');
    }
  };

  return (
    <Modal open={open} handleClose={onClose} width='sm'>
      <ModalHeader handleClose={onClose}>Cancel Ride</ModalHeader>

      <ModalContent>
        {/* Warning blurb */}
        <div className={cls.blurb}>
          <BiSolidError className={cls.blurbIcon} />
          <Typography variant='body2' sx={{ textWrap: 'pretty', color: theme.palette.error.dark }}>
            Are you sure you want to cancel the ride for <strong>{ride?.guest_name || 'this guest'}</strong>? This
            action cannot be undone.
          </Typography>
        </div>

        {/* Pickup / Dropoff details */}
        <Typography variant='body2' color='text.secondary' gutterBottom>
          <strong>Pickup:</strong> {ride?.pickup_address || 'No address provided'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          <strong>Dropoff:</strong> {ride?.dropoff_address || 'No address provided'}
        </Typography>
      </ModalContent>

      <ModalFooter>
        <Button disabled={loading} variant='text' color='utility' onClick={onClose}>
          Go Back
        </Button>
        <Button variant='contained' color='error' onClick={handleConfirm} disabled={loading}>
          {loading ? 'Cancelling...' : 'Cancel Ride'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  blurb: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${theme?.palette?.error?.light};
    border-radius: 10px;
    margin-bottom: 12px;
    background-color: ${theme?.palette?.error?.light}20;
  `,
  blurbIcon: css`
    min-width: 20px;
    min-height: 20px;
    font-size: 20px;
    color: ${theme?.palette?.error?.main};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default CancelRideModal;
